/**
 * Math Expressions Service
 * API client for mathematical expressions CRUD operations
 */

import apiClient from './api';

// ============================================================================
// Types
// ============================================================================

export interface DecomposedExpression {
  id: string;
  plainText: string;
  latex: string;
  spine: any;
  explanation: string;
  selected: boolean;
}

export interface ContentExpression {
  id: string;
  plainText: string;
  latex: string;
  spine: any;
  context: string;
  reasoning: string;
  confidence_score: number;
  selected: boolean;
}

export interface MathExpression {
  contentExpression: ContentExpression;
  decomposed: DecomposedExpression[];
}

export interface MathExpressionFile {
  pdf_name: string;
  content_id: string;
  processing_date: string;
  expressions: MathExpression[];
}

export interface MathExpressionStatus {
  lessonId: string;
  hasOriginal: boolean;
  hasModified: boolean;
  canEdit: boolean;
}

export interface SaveMathExpressionsResponse {
  success: boolean;
  message: string;
  lessonId: string;
  modifiedBy: string;
  modifiedAt: string;
  expressionCount: number;
}

export interface DeleteMathExpressionResponse {
  success: boolean;
  message: string;
  lessonId: string;
  expressionId: string;
  deletedBy: string;
  deletedAt: string;
  remainingExpressionCount: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get original math expressions for a lesson
 */
export const getMathExpressionsOriginal = async (
  lessonId: string
): Promise<MathExpressionFile> => {
  const encodedLessonId = encodeURIComponent(lessonId);
  const response = await apiClient.get(`/math-expressions/${encodedLessonId}/original`);
  return response.data;
};

/**
 * Get modified math expressions for a lesson
 */
export const getMathExpressionsModified = async (
  lessonId: string
): Promise<MathExpressionFile> => {
  const encodedLessonId = encodeURIComponent(lessonId);
  const response = await apiClient.get(`/math-expressions/${encodedLessonId}/modified`);
  return response.data;
};

/**
 * Get effective math expressions (modified if exists, otherwise original)
 */
export const getMathExpressionsEffective = async (
  lessonId: string
): Promise<MathExpressionFile> => {
  const encodedLessonId = encodeURIComponent(lessonId);
  const response = await apiClient.get(`/math-expressions/${encodedLessonId}/effective`);
  return response.data;
};

/**
 * Check math expressions status for a lesson
 */
export const getMathExpressionsStatus = async (
  lessonId: string
): Promise<MathExpressionStatus> => {
  const encodedLessonId = encodeURIComponent(lessonId);
  const response = await apiClient.get(`/math-expressions/${encodedLessonId}/status`);
  return response.data;
};

/**
 * Save modified math expressions for a lesson
 */
export const saveMathExpressionsModified = async (
  lessonId: string,
  expressions: MathExpression[]
): Promise<SaveMathExpressionsResponse> => {
  const encodedLessonId = encodeURIComponent(lessonId);
  const response = await apiClient.post(`/math-expressions/${encodedLessonId}/modified`, {
    expressions,
  });
  return response.data;
};

/**
 * Delete a math expression from the modified file and clean up tag prediction references
 */
export const deleteMathExpression = async (
  lessonId: string,
  expressionId: string
): Promise<DeleteMathExpressionResponse> => {
  const encodedLessonId = encodeURIComponent(lessonId);
  const encodedExpressionId = encodeURIComponent(expressionId);
  const response = await apiClient.delete(
    `/math-expressions/${encodedLessonId}/expression/${encodedExpressionId}`
  );
  return response.data;
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Merge original and modified expressions, prioritizing modified
 * Useful for displaying expressions with modifications overlay
 */
export const mergeExpressions = (
  original: MathExpression[],
  modified: MathExpression[]
): MathExpression[] => {
  const modifiedMap = new Map(modified.map(expr => [expr.contentExpression.id, expr]));

  // Start with original expressions, replacing with modified where available
  const merged = original.map(originalExpr => {
    const modifiedExpr = modifiedMap.get(originalExpr.contentExpression.id);
    return modifiedExpr || originalExpr;
  });

  // Add any new expressions that only exist in modified
  modified.forEach(modifiedExpr => {
    if (!original.find(orig => orig.contentExpression.id === modifiedExpr.contentExpression.id)) {
      merged.push(modifiedExpr);
    }
  });

  return merged;
};

/**
 * Check if two math expression arrays are different
 */
export const hasExpressionChanges = (
  original: MathExpression[],
  modified: MathExpression[]
): boolean => {
  if (original.length !== modified.length) {
    return true;
  }

  // Sort both arrays by ID for comparison
  const sortedOriginal = [...original].sort((a, b) =>
    a.contentExpression.id.localeCompare(b.contentExpression.id)
  );
  const sortedModified = [...modified].sort((a, b) =>
    a.contentExpression.id.localeCompare(b.contentExpression.id)
  );

  for (let i = 0; i < sortedOriginal.length; i++) {
    const orig = sortedOriginal[i].contentExpression;
    const mod = sortedModified[i].contentExpression;

    if (
      orig.id !== mod.id ||
      orig.latex !== mod.latex ||
      orig.selected !== mod.selected ||
      orig.reasoning !== mod.reasoning ||
      orig.context !== mod.context
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Generate a new unique ID for a math expression (UUID v4)
 */
export const generateExpressionId = (): string => {
  return crypto.randomUUID();
};

/**
 * Validate a math expression
 */
export const validateExpression = (expression: MathExpression): string[] => {
  const errors: string[] = [];
  const content = expression.contentExpression;

  if (!content.id || content.id.trim() === '') {
    errors.push('Expression ID is required');
  }

  if (!content.latex || content.latex.trim() === '') {
    errors.push('LaTeX expression is required');
  }

  if (typeof content.selected !== 'boolean') {
    errors.push('Selected status must be a boolean');
  }

  return errors;
};

/**
 * Validate an array of math expressions
 */
export const validateExpressions = (expressions: MathExpression[]): string[] => {
  const allErrors: string[] = [];
  const ids = new Set<string>();

  expressions.forEach((expr, index) => {
    const errors = validateExpression(expr);
    if (errors.length > 0) {
      allErrors.push(`Expression ${index + 1}: ${errors.join(', ')}`);
    }

    // Check for duplicate IDs
    const id = expr.contentExpression.id;
    if (ids.has(id)) {
      allErrors.push(`Duplicate ID found: ${id}`);
    }
    ids.add(id);
  });

  return allErrors;
};

// ============================================================================
// React Query Keys
// ============================================================================

export const mathExpressionKeys = {
  all: ['mathExpressions'] as const,
  lesson: (lessonId: string) => [...mathExpressionKeys.all, lessonId] as const,
  original: (lessonId: string) => [...mathExpressionKeys.lesson(lessonId), 'original'] as const,
  modified: (lessonId: string) => [...mathExpressionKeys.lesson(lessonId), 'modified'] as const,
  effective: (lessonId: string) => [...mathExpressionKeys.lesson(lessonId), 'effective'] as const,
  status: (lessonId: string) => [...mathExpressionKeys.lesson(lessonId), 'status'] as const,
};